import type { LocalizedCopy } from "../productCopyTypes";

function paragraphs(text: string): string {
  return text
    .split(/\n\n+/)
    .map((p) => p.trim())
    .filter(Boolean)
    .map((p) => `<p>\n${p}\n</p>`)
    .join("\n\n");
}

function featureList(items: string[]): string {
  return `<ul>\n${items.map((item) => `  <li>${item}</li>`).join("\n")}\n</ul>`;
}

const COPY = {
  en: {
    title: "Car series",
    description:
      "Bring the excitement of classic touring car racing to your collection with the Car series. This detailed brick-built set recreates a complete race weekend scene, featuring a race transporter, support van, and a classic touring race car inspired by legendary European motorsport.\n\nBuilt with 1,536 pieces, the model offers an enjoyable building experience while delivering an impressive display piece. Every vehicle features realistic proportions, detailed interiors, and functional elements that make the set perfect for both collectors and builders.\n\nThe transporter is equipped with folding loading ramps, allowing the race car to be loaded and unloaded just like the real thing. The support van includes opening side and rear doors, revealing a detailed interior ready to support the racing team. Additional accessories such as spare wheels, tools, workshop equipment, traffic cones, and a pallet jack complete the authentic paddock atmosphere.\n\nFinished in a striking white racing livery with classic motorsport-inspired graphics, the three vehicles create an eye-catching display whether arranged together or separately.\n\nWhether you're a fan of classic touring car racing or simply enjoy highly detailed vehicle models, the Car series is an outstanding centerpiece that captures the spirit of the racing paddock in brick form.",
    highlightsTitle: "Features",
    features: [
      "1,536 high-quality building pieces",
      "Three detailed vehicles in one set",
      "Race transporter with functional loading ramps",
      "Service van with opening side and rear doors",
      "Classic touring race car with detailed cockpit",
      "Workshop accessories and spare wheels included",
      "Display-worthy racing team livery",
      "Designed for collectors and motorsport enthusiasts",
    ],
    specsTitle: "Specifications",
    specs: [
      "<li><strong>Brand:</strong> 12GO</li>",
      "<li><strong>Set Number:</strong> 95021</li>",
      "<li><strong>Series:</strong> Car Series</li>",
      "<li><strong>Pieces:</strong> 1,536</li>",
      "<li><strong>Recommended Age:</strong> 14+</li>",
      "<li><strong>Theme:</strong> Motorsport / Racing Transporter</li>",
      "<li><strong>Material:</strong> ABS Plastic</li>",
    ],
  },
  pl: {
    title: "Car series",
    description:
      "Przenieś atmosferę klasycznych wyścigów samochodów turystycznych do swojej kolekcji dzięki Car series. Ten wyjątkowy zestaw przedstawia kompletny zespół wyścigowy, składający się z transportera, samochodu serwisowego oraz klasycznego auta wyścigowego inspirowanego legendarnymi europejskimi mistrzostwami samochodów turystycznych.\n\nModel złożony z 1536 elementów oferuje satysfakcjonujące doświadczenie budowania oraz imponujący model kolekcjonerski. Wszystkie pojazdy wyróżniają się realistycznymi proporcjami, bogatym wyposażeniem oraz licznymi funkcjonalnymi elementami.\n\nTransporter wyposażono w składane najazdy umożliwiające załadunek samochodu wyścigowego. Samochód serwisowy posiada otwierane drzwi boczne i tylne, odsłaniające szczegółowo odwzorowane wnętrze. Zestaw uzupełniają zapasowe koła, narzędzia, wyposażenie warsztatowe, pachołki drogowe oraz ręczny wózek paletowy, tworząc realistyczną atmosferę paddocku.\n\nCałość utrzymano w efektownym biało-niebieskim malowaniu inspirowanym klasycznymi zespołami wyścigowymi. Niezależnie od tego, czy modele zostaną ustawione razem, czy osobno, stanowią wyjątkową ozdobę każdej kolekcji.\n\nNiezależnie od tego, czy jesteś fanem klasycznych wyścigów samochodów turystycznych, czy po prostu cenisz dopracowane modele pojazdów, Car series będzie efektownym elementem każdej kolekcji, doskonale oddając atmosferę profesjonalnego paddocku wyścigowego.",
    highlightsTitle: "Cechy produktu",
    features: [
      "1536 wysokiej jakości elementów",
      "Trzy szczegółowo wykonane pojazdy w jednym zestawie",
      "Transporter z funkcjonalnymi najazdami",
      "Samochód serwisowy z otwieranymi drzwiami bocznymi i tylnymi",
      "Klasyczny samochód wyścigowy z bogato odwzorowanym kokpitem",
      "Narzędzia warsztatowe i zapasowe koła w zestawie",
      "Realistyczne malowanie inspirowane motorsportem",
      "Idealny dla kolekcjonerów i miłośników wyścigów samochodowych",
    ],
    specsTitle: "Specyfikacja",
    specs: [
      "<li><strong>Marka:</strong> 12GO</li>",
      "<li><strong>Numer zestawu:</strong> 95021</li>",
      "<li><strong>Seria:</strong> Car Series</li>",
      "<li><strong>Liczba elementów:</strong> 1536</li>",
      "<li><strong>Wiek:</strong> 14+</li>",
      "<li><strong>Tematyka:</strong> Motorsport / Transporter wyścigowy</li>",
      "<li><strong>Materiał:</strong> Tworzywo ABS</li>",
    ],
  },
  de: {
    title: "Car series",
    description:
      "Bringen Sie die Atmosphäre klassischer Tourenwagenrennen in Ihre Sammlung mit dem Car series. Dieses detailreiche Klemmbaustein-Set stellt ein komplettes Rennteam dar und umfasst einen Renntransporter, einen Service-Van sowie einen klassischen Tourenwagen im Stil legendärer europäischer Rennfahrzeuge.\n\nMit 1.536 Bausteinen bietet das Modell ein anspruchsvolles Bauerlebnis und ein beeindruckendes Ausstellungsstück. Alle Fahrzeuge überzeugen durch realistische Proportionen, zahlreiche Details und funktionale Elemente.\n\nDer Transporter verfügt über klappbare Auffahrrampen zum Verladen des Rennwagens. Der Service-Van besitzt zu öffnende Seiten- und Hecktüren mit detailliertem Innenraum. Ersatzräder, Werkzeug, Werkstattausrüstung, Verkehrskegel und ein Hubwagen vervollständigen die Rennstrecken-Atmosphäre.",
    highlightsTitle: "Merkmale",
    features: [
      "1.536 hochwertige Bausteine",
      "Drei detaillierte Fahrzeuge in einem Set",
      "Renntransporter mit klappbaren Auffahrrampen",
      "Service-Van mit zu öffnenden Türen",
      "Klassischer Tourenwagen mit detailliertem Cockpit",
      "Zubehör wie Ersatzräder und Werkstattausrüstung",
      "Attraktive Rennlackierung",
      "Ideal für Sammler und Motorsportfans",
    ],
    specsTitle: "Spezifikationen",
    specs: [
      "<li><strong>Marke:</strong> 12GO</li>",
      "<li><strong>Set-Nummer:</strong> 95021</li>",
      "<li><strong>Serie:</strong> Car Series</li>",
      "<li><strong>Teile:</strong> 1.536</li>",
      "<li><strong>Empfohlenes Alter:</strong> 14+</li>",
      "<li><strong>Thema:</strong> Motorsport / Racing Transporter</li>",
      "<li><strong>Material:</strong> ABS-Kunststoff</li>",
    ],
  },
  es: {
    title: "Car series",
    description:
      "Lleva la emoción del automovilismo clásico a tu colección con el Car series. Este detallado set incluye un camión portacoches, una furgoneta de asistencia y un coche de competición inspirado en los legendarios turismos europeos.\n\nCon 1.536 piezas, ofrece una experiencia de construcción entretenida y un impresionante modelo de exposición. Todos los vehículos cuentan con proporciones realistas, gran nivel de detalle y elementos funcionales.\n\nEl transportador incorpora rampas abatibles para cargar el coche de carreras. La furgoneta dispone de puertas laterales y traseras practicables, mientras que las ruedas de repuesto, herramientas y accesorios recrean un auténtico paddock de competición.",
    highlightsTitle: "Características",
    features: [
      "1.536 piezas de alta calidad",
      "Tres vehículos detallados",
      "Plataforma de transporte con rampas funcionales",
      "Furgoneta con puertas abatibles",
      "Coche de carreras clásico con gran nivel de detalle",
      "Accesorios de taller incluidos",
      "Decoración inspirada en el automovilismo",
      "Perfecto para coleccionistas y aficionados al motor",
    ],
    specsTitle: "Especificaciones",
    specs: [
      "<li><strong>Marca:</strong> 12GO</li>",
      "<li><strong>Número de set:</strong> 95021</li>",
      "<li><strong>Serie:</strong> Car Series</li>",
      "<li><strong>Piezas:</strong> 1.536</li>",
      "<li><strong>Edad recomendada:</strong> 14+</li>",
      "<li><strong>Tema:</strong> Motorsport / Racing Transporter</li>",
      "<li><strong>Material:</strong> plástico ABS</li>",
    ],
  },
  fr: {
    title: "Car series",
    description:
      "Retrouvez l'ambiance des courses de tourisme classiques avec le Car series. Ce coffret reproduit une équipe de course complète comprenant un camion porte-voiture, un fourgon d'assistance et une voiture de tourisme inspirée des légendes du sport automobile européen.\n\nComposé de 1 536 pièces, ce modèle offre une expérience de construction passionnante ainsi qu'une superbe pièce d'exposition. Chaque véhicule présente des proportions réalistes, de nombreux détails et des fonctionnalités fidèles.\n\nLe transporteur est équipé de rampes rabattables permettant de charger facilement la voiture de course. Le fourgon dispose de portes latérales et arrière ouvrantes, tandis que des roues de rechange, des outils, du matériel d'atelier, des cônes et un transpalette complètent l'ensemble.",
    highlightsTitle: "Caractéristiques",
    features: [
      "1 536 pièces de haute qualité",
      "Trois véhicules détaillés",
      "Porte-voiture avec rampes fonctionnelles",
      "Fourgon avec portes ouvrantes",
      "Voiture de course classique détaillée",
      "Nombreux accessoires d'atelier",
      "Décoration inspirée du sport automobile",
      "Idéal pour les collectionneurs",
    ],
    specsTitle: "Spécifications",
    specs: [
      "<li><strong>Marque :</strong> 12GO</li>",
      "<li><strong>Numéro de set :</strong> 95021</li>",
      "<li><strong>Série :</strong> Car Series</li>",
      "<li><strong>Pièces :</strong> 1 536</li>",
      "<li><strong>Âge recommandé :</strong> 14+</li>",
      "<li><strong>Thème :</strong> Motorsport / Racing Transporter</li>",
      "<li><strong>Matériau :</strong> plastique ABS</li>",
    ],
  },
  nl: {
    title: "Car series",
    description:
      "Breng de sfeer van klassieke toerwagenraces naar je collectie met de Car series. Deze uitgebreide bouwset bevat een autotransporter, een servicemodel en een klassieke raceauto, geïnspireerd op legendarische Europese toerwagens.\n\nMet 1.536 onderdelen biedt deze set een uitdagende bouwervaring en een indrukwekkend displaymodel. Alle voertuigen zijn voorzien van realistische details en functionele elementen.\n\nDe transporter beschikt over neerklapbare oprijplaten, terwijl de servicebus openslaande zij- en achterdeuren heeft. Reservewielen, gereedschap en werkplaatsaccessoires maken het racepaddock compleet.",
    highlightsTitle: "Kenmerken",
    features: [
      "1.536 hoogwaardige bouwstenen",
      "Drie gedetailleerde voertuigen",
      "Transporter met functionele oprijplaten",
      "Servicebus met openende deuren",
      "Klassieke raceauto met gedetailleerd interieur",
      "Inclusief werkplaatsaccessoires",
      "Motorsport-geïnspireerde kleurstelling",
      "Perfect voor verzamelaars en autosportliefhebbers",
    ],
    specsTitle: "Specificaties",
    specs: [
      "<li><strong>Merk:</strong> 12GO</li>",
      "<li><strong>Setnummer:</strong> 95021</li>",
      "<li><strong>Serie:</strong> Car Series</li>",
      "<li><strong>Onderdelen:</strong> 1.536</li>",
      "<li><strong>Aanbevolen leeftijd:</strong> 14+</li>",
      "<li><strong>Thema:</strong> Motorsport / Racing Transporter</li>",
      "<li><strong>Materiaal:</strong> ABS-plastic</li>",
    ],
  },
  zh: {
    title: "Car series",
    description:
      "通过 Car series，将经典房车赛事的激情带入您的积木收藏。这款精致的模型完整再现了一支专业赛车车队，包括一辆赛车运输车、一辆维修服务车以及一辆灵感源自欧洲经典房车赛事的赛车。\n\n整套模型由 1,536 块积木组成，不仅拥有出色的拼搭体验，更是一件极具展示价值的收藏作品。所有车辆均采用真实比例设计，拥有丰富细节和多种可动功能。\n\n运输车配备可折叠装载坡道，可轻松装载赛车；维修服务车拥有可开启的侧门和后门，内部结构细节丰富。套装还包含备用轮胎、维修工具、车间设备、交通锥以及液压托盘车等配件，完整还原专业赛车维修区（Paddock）的工作场景。\n\n整套模型采用经典的白蓝赛车涂装，灵感来自传奇赛车车队，无论整体展示还是单独陈列，都极具视觉冲击力，是赛车爱好者不可错过的收藏佳作。\n\n无论您是经典赛车运动的忠实粉丝，还是热爱高品质积木模型，Car series都能为您的收藏带来专业赛车维修区的真实氛围，并成为展示柜中的亮点。",
    highlightsTitle: "产品特点",
    features: [
      "1,536 块高品质积木",
      "一套包含三款精致车辆",
      "赛车运输车配备可折叠装载坡道",
      "维修服务车拥有可开启侧门和尾门",
      "经典赛车配备精细驾驶舱",
      "附带备用轮胎、维修工具及车间配件",
      "经典赛车风格涂装",
      "专为赛车爱好者和收藏家打造",
    ],
    specsTitle: "产品规格",
    specs: [
      "<li><strong>品牌：</strong>12GO</li>",
      "<li><strong>产品编号：</strong>95021</li>",
      "<li><strong>系列：</strong>Car Series</li>",
      "<li><strong>零件数量：</strong>1,536 块</li>",
      "<li><strong>建议年龄：</strong>14 岁以上</li>",
      "<li><strong>主题：</strong>赛车 / 赛车运输车</li>",
      "<li><strong>材质：</strong>ABS 塑料</li>",
    ],
  },
  it: {
    title: "Serie auto",
    description:
      "Porta nella tua collezione l'emozione delle gare automobilistiche turismo d'epoca con la Serie auto. Questo dettagliato set costruibile con mattoncini ricrea una scena completa di un weekend di gara, con un trasportatore da competizione, un furgone di assistenza e una classica auto da corsa turismo ispirata al leggendario motorsport europeo.\n\nComposto da 1.536 pezzi, il modello offre un'esperienza di costruzione appagante e un magnifico elemento da esposizione. Ogni veicolo presenta proporzioni realistiche, interni dettagliati ed elementi funzionali che rendono il set perfetto sia per i collezionisti sia per gli appassionati di costruzioni.\n\nIl trasportatore è dotato di rampe di carico pieghevoli, che consentono di caricare e scaricare l'auto da corsa proprio come nella realtà. Il furgone di assistenza include porte laterali e posteriori apribili, che rivelano un interno dettagliato pronto a supportare il team di gara. Accessori aggiuntivi come ruote di scorta, attrezzi, attrezzatura da officina, coni stradali e un transpallet completano l'autentica atmosfera del paddock.\n\nRifiniti con una sorprendente livrea da gara bianca e grafiche ispirate al motorsport classico, i tre veicoli creano un'esposizione d'impatto, sia disposti insieme sia separatamente.\n\nChe tu sia un appassionato delle gare turismo d'epoca o semplicemente ami i modelli di veicoli altamente dettagliati, la Serie auto è un eccezionale elemento centrale che cattura lo spirito del paddock in forma di mattoncini.",
    highlightsTitle: "Caratteristiche",
    features: [
      "1.536 pezzi da costruzione di alta qualità",
      "Tre veicoli dettagliati in un unico set",
      "Trasportatore da gara con rampe di carico funzionali",
      "Furgone di assistenza con porte laterali e posteriori apribili",
      "Classica auto da corsa turismo con abitacolo dettagliato",
      "Accessori da officina e ruote di scorta inclusi",
      "Livrea del team da gara degna di esposizione",
      "Progettato per collezionisti e appassionati di motorsport",
    ],
    specsTitle: "Specifiche",
    specs: [
      "<li><strong>Marca:</strong> 12GO</li>",
      "<li><strong>Numero del set:</strong> 95021</li>",
      "<li><strong>Serie:</strong> Serie auto</li>",
      "<li><strong>Pezzi:</strong> 1.536</li>",
      "<li><strong>Età consigliata:</strong> 14+</li>",
      "<li><strong>Tema:</strong> Motorsport / Trasportatore da gara</li>",
      "<li><strong>Materiale:</strong> Plastica ABS</li>",
    ],
  },
  ru: {
    title: "Автомобильная серия",
    description:
      "Добавьте в свою коллекцию атмосферу классических кузовных автогонок с Автомобильной серией. Этот детализированный набор из конструктора воссоздаёт полноценную сцену гоночного уик-энда: транспортер для гоночного автомобиля, сервисный фургон и классический кузовной гоночный автомобиль, вдохновлённый легендарным европейским автоспортом.\n\nМодель из 1 536 деталей подарит увлекательный процесс сборки и станет впечатляющим экспонатом. Каждый автомобиль отличается реалистичными пропорциями, детализированным интерьером и функциональными элементами, благодаря чему набор отлично подойдёт как коллекционерам, так и любителям конструирования.\n\nТранспортер оснащён складными погрузочными рампами, позволяющими загружать и выгружать гоночный автомобиль совсем как в настоящей жизни. У сервисного фургона открываются боковые и задние двери, за которыми скрывается детализированный интерьер, готовый обслуживать гоночную команду. Запасные колёса, инструменты, оборудование мастерской, дорожные конусы и гидравлическая тележка дополняют аутентичную атмосферу пит-лейна.\n\nТри автомобиля выполнены в эффектной белой гоночной ливрее с графикой в стиле классического автоспорта и впечатляюще смотрятся как вместе, так и по отдельности.\n\nЕсли вы увлекаетесь классическими кузовными гонками или просто любите высокодетализированные модели автомобилей, Автомобильная серия станет великолепным центральным элементом коллекции, передающим дух гоночного пит-лейна в формате конструктора.",
    highlightsTitle: "Особенности",
    features: [
      "1 536 высококачественных деталей конструктора",
      "Три детализированных автомобиля в одном наборе",
      "Гоночный транспортер с функциональными погрузочными рампами",
      "Сервисный фургон с открывающимися боковыми и задними дверями",
      "Классический кузовной гоночный автомобиль с детализированной кабиной",
      "Аксессуары для мастерской и запасные колёса в комплекте",
      "Эффектная ливрея гоночной команды, достойная экспозиции",
      "Создано для коллекционеров и поклонников автоспорта",
    ],
    specsTitle: "Характеристики",
    specs: [
      "<li><strong>Бренд:</strong> 12GO</li>",
      "<li><strong>Номер набора:</strong> 95021</li>",
      "<li><strong>Серия:</strong> Автомобильная серия</li>",
      "<li><strong>Детали:</strong> 1 536</li>",
      "<li><strong>Рекомендуемый возраст:</strong> 14+</li>",
      "<li><strong>Тематика:</strong> Автоспорт / Гоночный транспортер</li>",
      "<li><strong>Материал:</strong> АБС-пластик</li>",
    ],
  },

};

type LocaleKey = keyof typeof COPY;

function buildLocaleHtml(locale: LocaleKey): string {
  const c = COPY[locale] ?? COPY.en;
  return `<h1>${c.title}</h1>

${paragraphs(c.description)}

<h2>${c.specsTitle}</h2>

<ul>
${c.specs.join("\n")}
</ul>

<h2>${c.highlightsTitle}</h2>

${featureList([...c.features])}`;
}

export const RICH_PRODUCT_COPY_95021: LocalizedCopy = {
  en: buildLocaleHtml("en"),
  pl: buildLocaleHtml("pl"),
  de: buildLocaleHtml("de"),
  es: buildLocaleHtml("es"),
  fr: buildLocaleHtml("fr"),
  nl: buildLocaleHtml("nl"),
  zh: buildLocaleHtml("zh"),
  it: buildLocaleHtml("it"),
  ru: buildLocaleHtml("ru"),
};
